import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { N8AOPass } from 'n8ao';
import { isFloorPlanAmbientOcclusionEnabled } from './floorPlanThreeRenderConfig.js';

/**
 * @param {THREE.WebGLRenderer} renderer
 * @param {THREE.Scene} scene
 * @param {THREE.Camera} camera
 * @param {number} width
 * @param {number} height
 * @param {import('./floorPlanThreeRenderConfig.js').FLOOR_PLAN_RENDER_QUALITY['ambientOcclusion']} aoConfig
 * @returns {{ composer: EffectComposer; n8aoPass: N8AOPass } | null}
 */
export function createFloorPlanPostProcessing(renderer, scene, camera, width, height, aoConfig) {
  if (!aoConfig?.enabled || !isFloorPlanAmbientOcclusionEnabled()) {
    return null;
  }

  const composer = new EffectComposer(renderer);
  const n8aoPass = new N8AOPass(scene, camera, width, height);
  n8aoPass.renderToScreen = true;

  if (aoConfig.qualityMode) {
    n8aoPass.setQualityMode(aoConfig.qualityMode);
  }

  n8aoPass.configuration.aoRadius = aoConfig.radius;
  n8aoPass.configuration.intensity = aoConfig.intensity;
  n8aoPass.configuration.distanceFalloff = aoConfig.distanceFalloff;
  n8aoPass.configuration.halfRes = aoConfig.halfRes;
  n8aoPass.configuration.color = new THREE.Color(0, 0, 0);
  // N8AOPass is the final output pass; gammaCorrection encodes linear beauty to sRGB once.
  n8aoPass.configuration.gammaCorrection = true;
  n8aoPass.configuration.screenSpaceRadius = false;
  n8aoPass.configuration.colorMultiply = true;
  n8aoPass.setDisplayMode('Combined');

  if (process.env.NODE_ENV === 'development') {
    console.debug('[FloorPlan3D] N8AO settings', {
      aoRadius: n8aoPass.configuration.aoRadius,
      intensity: n8aoPass.configuration.intensity,
      distanceFalloff: n8aoPass.configuration.distanceFalloff,
      halfRes: n8aoPass.configuration.halfRes,
      qualityMode: aoConfig.qualityMode,
      gammaCorrection: n8aoPass.configuration.gammaCorrection,
    });
  }

  composer.addPass(n8aoPass);

  return { composer, n8aoPass };
}

/**
 * Toggle N8AO compositing without remounting the scene (dev comparison).
 * @param {{ n8aoPass?: import('n8ao').N8AOPass | null }} postProcessing
 * @param {boolean} enabled
 */
export function setFloorPlanAmbientOcclusionPreview(postProcessing, enabled) {
  postProcessing?.n8aoPass?.setDisplayMode(enabled ? 'Combined' : 'No AO');
}

/**
 * @param {{ composer?: EffectComposer | null; renderer: THREE.WebGLRenderer; scene: THREE.Scene; camera: THREE.Camera }} sceneState
 */
export function renderFloorPlanFrame(sceneState) {
  if (!sceneState || sceneState.isDisposed) return;
  if (sceneState.composer) {
    sceneState.composer.render();
    return;
  }
  sceneState.renderer.render(sceneState.scene, sceneState.camera);
}

/**
 * @param {{ composer?: EffectComposer | null; renderer: THREE.WebGLRenderer }} sceneState
 * @param {number} width
 * @param {number} height
 */
export function resizeFloorPlanPostProcessing(sceneState, width, height) {
  sceneState.renderer.setSize(width, height);
  sceneState.composer?.setSize(width, height);
}

/**
 * @param {{ composer?: EffectComposer | null; n8aoPass?: N8AOPass | null }} postProcessing
 */
export function disposeFloorPlanPostProcessing(postProcessing) {
  if (!postProcessing) return;

  const { composer, n8aoPass } = postProcessing;
  if (n8aoPass) {
    n8aoPass.beautyRenderTarget?.dispose();
    n8aoPass.writeTargetInternal?.dispose();
    n8aoPass.readTargetInternal?.dispose();
    n8aoPass.accumulationRenderTarget?.dispose();
    n8aoPass.depthDownsampleTarget?.dispose();
    n8aoPass.transparencyRenderTargetDWFalse?.dispose();
    n8aoPass.transparencyRenderTargetDWTrue?.dispose();
    n8aoPass.effectShaderQuad?.material?.dispose();
    n8aoPass.poissonBlurQuad?.material?.dispose();
    n8aoPass.effectCompositerQuad?.material?.dispose();
    n8aoPass.depthDownsampleQuad?.material?.dispose();
    n8aoPass.accumulationQuad?.material?.dispose();
  }

  if (composer) {
    composer.renderTarget1?.dispose();
    composer.renderTarget2?.dispose();
    composer.copyPass?.dispose?.();
  }
}
